import React, { useContext, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { green, orange, red } from "@material-ui/core/colors";
import { useHistory } from "react-router-dom";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  menuPaper: {
    minWidth: 280,
    maxWidth: 360,
    maxHeight: 420,
  },
  row: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(0.5),
    width: "100%",
    paddingTop: 4,
    paddingBottom: 4,
  },
  title: {
    fontWeight: 600,
    padding: theme.spacing(1, 2, 0.5),
  },
}));

/** Enchufe “enchufado” (al menos una conexión activa o cargando). */
function IconPlugConnected({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{ display: "block" }}
    >
      <path
        d="M12 22v-5M9 8V2M15 8V2M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Enchufe desconectado (ninguna conexión activa). */
function IconPlugDisconnected({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{ display: "block" }}
    >
      <path
        d="m19 5 3-3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="m2 22 3-3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.3 20.3a2.4 2.4 0 0 1-3-3L12 8l-3-3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 18h6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 22h4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function statusMeta(status) {
  const u = String(status || "").toUpperCase();
  if (u === "CONNECTED") {
    return { label: i18n.t("whatsappConnectionsMenu.status.connected"), chip: green[500], text: "#fff" };
  }
  if (u === "OPENING") {
    return { label: i18n.t("whatsappConnectionsMenu.status.opening"), chip: orange[700], text: "#fff" };
  }
  return { label: i18n.t("whatsappConnectionsMenu.status.disconnected"), chip: red[100], text: red[900] };
}

/**
 * Icono en la barra: enchufe (verde) si ≥1 conexión CONNECTED; enchufe desconectado (rojo) si ninguna.
 * Clic: menú de conexiones y enlace a /connections.
 */
const WhatsAppConnectionsMenu = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const [anchorEl, setAnchorEl] = useState(null);

  const list = Array.isArray(whatsApps) ? whatsApps : [];
  const anyConnected = list.some((w) => String(w.status || "").toUpperCase() === "CONNECTED");

  /** Forma de enchufe conectado si: carga o hay ≥1 CONNECTED; si no, icono desenchufado en rojo. */
  const showPlugConnectedShape = loading || anyConnected;

  let iconColor;
  if (loading) {
    iconColor = theme.palette.text.secondary;
  } else if (list.length === 0) {
    iconColor = red[500];
  } else {
    iconColor = anyConnected ? green[500] : red[500];
  }

  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const goManage = () => {
    handleClose();
    history.push("/connections");
  };

  return (
    <>
      <Tooltip
        title={
          loading
            ? i18n.t("whatsappConnectionsMenu.loadingConnections")
            : list.length === 0
            ? i18n.t("whatsappConnectionsMenu.tooltipNoConnections")
            : anyConnected
            ? i18n.t("whatsappConnectionsMenu.tooltipSomeConnected")
            : i18n.t("whatsappConnectionsMenu.tooltipNoneActive")
        }
        arrow
      >
        <IconButton
          onClick={handleOpen}
          size="small"
          aria-label={i18n.t("whatsappConnectionsMenu.ariaLabel")}
          aria-haspopup="true"
          aria-expanded={open}
          style={{
            color: iconColor,
          }}
        >
          {showPlugConnectedShape ? <IconPlugConnected size={20} /> : <IconPlugDisconnected size={20} />}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        classes={{ paper: classes.menuPaper }}
      >
        <Typography variant="subtitle2" className={classes.title} color="textPrimary">
          {i18n.t("whatsappConnectionsMenu.menuTitle")}
        </Typography>
        <Divider />
        {loading && (
          <MenuItem disabled dense>
            {i18n.t("whatsappConnectionsMenu.loading")}
          </MenuItem>
        )}
        {!loading && list.length === 0 && (
          <MenuItem disabled dense>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("whatsappConnectionsMenu.noneConfigured")}
            </Typography>
          </MenuItem>
        )}
        {!loading &&
          list.map((w) => {
            const meta = statusMeta(w.status);
            return (
              <MenuItem key={w.id} dense disabled>
                <Box className={classes.row}>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {w.name || i18n.t("whatsappConnectionsMenu.connectionFallback", { id: w.id })}
                  </Typography>
                  {w.number && (
                    <Typography variant="caption" color="textSecondary" style={{ fontFamily: "monospace" }}>
                      {w.number}
                    </Typography>
                  )}
                  <Chip
                    size="small"
                    label={meta.label}
                    style={{
                      backgroundColor: meta.chip,
                      color: meta.text,
                      height: 22,
                      fontSize: "0.7rem",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </MenuItem>
            );
          })}
        <Divider />
        <MenuItem dense onClick={goManage}>
          {i18n.t("whatsappConnectionsMenu.manage")}
        </MenuItem>
      </Menu>
    </>
  );
};

export default WhatsAppConnectionsMenu;
